import { useState, useEffect } from 'react';

/**
 * Debounces a value by delaying updates until the value has been stable
 * for the specified delay period. Prevents API spam on every keystroke.
 *
 * @example
 * ```tsx
 * const [searchTerm, setSearchTerm] = useState('');
 * const debouncedSearch = useDebounce(searchTerm, 300);
 *
 * // User types "João"
 * // searchTerm updates immediately: "J" → "Jo" → "Joã" → "João"
 * // debouncedSearch updates only after 300ms of no typing: "João"
 * ```
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300ms)
 * @returns Debounced value
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    // Set up timeout to update debounced value after delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clear timeout if value changes before delay completes (cleanup function)
    // This prevents multiple API calls when user is actively typing
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}
